import { ExternalLink, Globe, Linkedin, Mail, MapPin, Phone, Save, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { EmailStatusBadge, LeadStatusBadge } from '../../components/ui/data';
import { Drawer, useToast } from '../../components/ui/overlays';
import {
  Badge,
  Button,
  Field,
  Input,
  Select,
  Textarea,
} from '../../components/ui/primitives';
import { useDeleteLead, useLead, useLeadTypes, useSaveLead } from '../../hooks/queries';
import { formatDateTime, formatRelative, parseTags } from '../../lib/utils';
import { LEAD_STATUSES, type Lead } from '../../types/api';

const EDITABLE = [
  { key: 'companyName', label: 'Company', type: 'text' },
  { key: 'contactName', label: 'Contact name', type: 'text' },
  { key: 'title', label: 'Job title', type: 'text' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'phone', label: 'Phone', type: 'text' },
  { key: 'website', label: 'Website', type: 'text' },
  { key: 'linkedinUrl', label: 'LinkedIn', type: 'text' },
  { key: 'country', label: 'Country', type: 'text' },
  { key: 'city', label: 'City', type: 'text' },
  { key: 'industry', label: 'Industry', type: 'text' },
  { key: 'companySize', label: 'Company size', type: 'text' },
  { key: 'language', label: 'Language', type: 'text' },
] as const;

export const LeadDrawer = ({
  leadId,
  projectId,
  onClose,
}: {
  leadId: string | null;
  projectId: string;
  onClose: () => void;
}) => {
  const { data: lead } = useLead(leadId ?? undefined);
  const { data: leadTypes } = useLeadTypes(projectId);
  const saveLead = useSaveLead(projectId);
  const deleteLead = useDeleteLead(projectId);
  const toast = useToast();

  const [draft, setDraft] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (!lead) return;
    const next: Record<string, unknown> = {};
    for (const field of EDITABLE) next[field.key] = (lead as unknown as Record<string, unknown>)[field.key] ?? '';
    next.status = lead.status;
    next.leadTypeId = lead.leadTypeId ?? '';
    next.score = lead.score ?? '';
    next.tags = lead.tags ?? '';
    next.rationale = lead.rationale ?? '';
    next.notes = lead.notes ?? '';
    setDraft(next);
  }, [lead]);

  const dirty =
    Boolean(lead) &&
    Object.entries(draft).some(([key, value]) => {
      const current = (lead as unknown as Record<string, unknown>)?.[key];
      return (current ?? '') !== value;
    });

  const save = async () => {
    if (!lead) return;
    try {
      await saveLead.mutateAsync({
        id: lead.id,
        ...(draft as Partial<Lead>),
        score: draft.score === '' ? undefined : Number(draft.score),
        leadTypeId: (draft.leadTypeId as string) || undefined,
      });
      toast.success('Lead saved');
    } catch (error) {
      toast.error(error);
    }
  };

  const remove = async () => {
    if (!lead) return;
    try {
      await deleteLead.mutateAsync(lead.id);
      toast.success('Lead deleted');
      onClose();
    } catch (error) {
      toast.error(error);
    }
  };

  return (
    <Drawer
      open={Boolean(leadId)}
      onClose={onClose}
      width="lg"
      title={lead?.companyName ?? 'Lead'}
      subtitle={
        lead ? (
          <span className="flex flex-wrap items-center gap-2">
            <LeadStatusBadge status={lead.status} />
            {lead.leadType ? <Badge dot={lead.leadType.color}>{lead.leadType.name}</Badge> : null}
            {lead.score !== null ? <Badge tone="brand">score {lead.score}</Badge> : null}
            <span className="text-2xs text-faint">added {formatRelative(lead.createdAt)}</span>
          </span>
        ) : null
      }
      footer={
        <>
          <Button
            variant="ghost"
            icon={<Trash2 className="h-4 w-4" />}
            onClick={remove}
            loading={deleteLead.isPending}
          >
            Delete
          </Button>
          <div className="flex-1" />
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={save}
            disabled={!dirty}
            loading={saveLead.isPending}
            icon={<Save className="h-4 w-4" />}
          >
            Save
          </Button>
        </>
      }
    >
      {!lead ? (
        <div className="p-5 text-[13px] text-faint">Loading…</div>
      ) : (
        <div className="divide-y divide-line">
          <div className="flex flex-wrap gap-3 px-5 py-4 text-[13px]">
            {lead.email ? (
              <a
                href={`mailto:${lead.email}`}
                className="flex items-center gap-1.5 text-brand hover:underline"
              >
                <Mail className="h-3.5 w-3.5" /> {lead.email}
              </a>
            ) : (
              <span className="flex items-center gap-1.5 text-faint">
                <Mail className="h-3.5 w-3.5" /> no email yet
              </span>
            )}
            {lead.website ? (
              <a
                href={lead.website}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 text-brand hover:underline"
              >
                <Globe className="h-3.5 w-3.5" /> {lead.domain ?? lead.website}
              </a>
            ) : null}
            {lead.linkedinUrl ? (
              <a
                href={lead.linkedinUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 text-brand hover:underline"
              >
                <Linkedin className="h-3.5 w-3.5" /> LinkedIn
              </a>
            ) : null}
            {lead.phone ? (
              <span className="flex items-center gap-1.5 text-muted">
                <Phone className="h-3.5 w-3.5" /> {lead.phone}
              </span>
            ) : null}
            {lead.country ? (
              <span className="flex items-center gap-1.5 text-muted">
                <MapPin className="h-3.5 w-3.5" /> {[lead.city, lead.country].filter(Boolean).join(', ')}
              </span>
            ) : null}
          </div>

          {lead.rationale ? (
            <div className="px-5 py-4">
              <span className="field-label">Why this lead fits</span>
              <p className="rounded-lg bg-raised px-3 py-2.5 text-[13px] leading-relaxed text-ink">
                {lead.rationale}
              </p>
              <p className="hint">
                Written by the research agent. The outreach agent uses this to personalise the email.
              </p>
            </div>
          ) : null}

          <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
            {EDITABLE.map((field) => (
              <Field key={field.key} label={field.label} htmlFor={`lead-${field.key}`}>
                <Input
                  id={`lead-${field.key}`}
                  type={field.type}
                  value={(draft[field.key] as string) ?? ''}
                  onChange={(event) => setDraft({ ...draft, [field.key]: event.target.value })}
                />
              </Field>
            ))}

            <Field label="Status" htmlFor="lead-status">
              <Select
                id="lead-status"
                value={(draft.status as string) ?? 'NEW'}
                onChange={(event) => setDraft({ ...draft, status: event.target.value })}
              >
                {LEAD_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Lead type" htmlFor="lead-type">
              <Select
                id="lead-type"
                value={(draft.leadTypeId as string) ?? ''}
                onChange={(event) => setDraft({ ...draft, leadTypeId: event.target.value })}
              >
                <option value="">Unassigned</option>
                {(leadTypes ?? []).map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Score" htmlFor="lead-score" hint="0-100 against the ICP">
              <Input
                id="lead-score"
                type="number"
                min={0}
                max={100}
                value={(draft.score as number | string) ?? ''}
                onChange={(event) => setDraft({ ...draft, score: event.target.value })}
              />
            </Field>

            <Field label="Tags" htmlFor="lead-tags" hint="Comma separated">
              <Input
                id="lead-tags"
                value={(draft.tags as string) ?? ''}
                onChange={(event) => setDraft({ ...draft, tags: event.target.value })}
              />
            </Field>

            <Field label="Rationale" htmlFor="lead-rationale" className="sm:col-span-2">
              <Textarea
                id="lead-rationale"
                rows={3}
                value={(draft.rationale as string) ?? ''}
                onChange={(event) => setDraft({ ...draft, rationale: event.target.value })}
              />
            </Field>

            <Field label="Notes" htmlFor="lead-notes" className="sm:col-span-2">
              <Textarea
                id="lead-notes"
                rows={3}
                value={(draft.notes as string) ?? ''}
                onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
              />
            </Field>
          </div>

          <div className="px-5 py-4">
            <span className="field-label">Provenance</span>
            <dl className="grid gap-x-4 gap-y-1.5 text-xs sm:grid-cols-2">
              <div className="flex justify-between gap-2">
                <dt className="text-faint">Source</dt>
                <dd className="text-ink">{lead.source ?? '—'}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-faint">Times contacted</dt>
                <dd className="tabular-nums text-ink">{lead.timesContacted}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-faint">Dedupe key</dt>
                <dd className="truncate font-mono text-ink" title={lead.dedupeKey}>
                  {lead.dedupeKey}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-faint">Last contacted</dt>
                <dd className="text-ink">{formatDateTime(lead.lastContactedAt)}</dd>
              </div>
              {lead.discoveryQuery ? (
                <div className="flex justify-between gap-2 sm:col-span-2">
                  <dt className="shrink-0 text-faint">Found via</dt>
                  <dd className="truncate text-right text-ink" title={lead.discoveryQuery}>
                    {lead.discoveryQuery}
                  </dd>
                </div>
              ) : null}
              {lead.sourceUrl ? (
                <div className="flex justify-between gap-2 sm:col-span-2">
                  <dt className="shrink-0 text-faint">Source page</dt>
                  <dd className="truncate text-right">
                    <a
                      href={lead.sourceUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-brand hover:underline"
                    >
                      {lead.sourceUrl.replace(/^https?:\/\//, '').slice(0, 48)}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </dd>
                </div>
              ) : null}
            </dl>
          </div>

          <div className="px-5 py-4">
            <span className="field-label">Conversation ({lead.emails?.length ?? 0})</span>
            {lead.emails?.length ? (
              <div className="space-y-2">
                {lead.emails.map((email) => (
                  <div key={email.id} className="rounded-lg border border-line px-3 py-2">
                    <div className="flex items-start justify-between gap-2">
                      <span className="min-w-0 truncate text-[13px] font-medium text-ink">
                        {email.direction === 'INBOUND' ? '← ' : '→ '}
                        {email.subject}
                      </span>
                      <EmailStatusBadge status={email.status} />
                    </div>
                    {email.snippet ? (
                      <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-muted">
                        {email.snippet}
                      </p>
                    ) : null}
                    <div className="mt-1 flex items-center gap-3 text-2xs text-faint">
                      <span>
                        {formatRelative(email.sentAt ?? email.receivedAt ?? email.createdAt)}
                      </span>
                      {email.openCount > 0 ? <span>{email.openCount} opens</span> : null}
                      {email.clickCount > 0 ? <span>{email.clickCount} clicks</span> : null}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[13px] text-faint">Nothing sent yet.</p>
            )}
          </div>

          {lead.tags ? (
            <div className="px-5 py-4">
              <span className="field-label">Tags</span>
              <div className="flex flex-wrap gap-1.5">
                {parseTags(lead.tags).map((tag) => (
                  <Badge key={tag}>{tag}</Badge>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Drawer>
  );
};
